import {useState, useEffect, useCallback, useMemo, memo} from 'react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@common-web/components/ui/table';
import {Plus, X, ArrowDown, Zap, Eye, Check, Info} from 'lucide-react';
import {PayrollFileValueTransformation} from '@api/lib/payrollProcessing/types';
import {applyValueTransformations} from '../utils/transformationUtils';
import {VALUE_TRANSFORMATION_OPTIONS} from '@client/pages/payroll/constants/transformationOptions';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@common-web/components/ui/sheet';
import {ScrollArea} from '@common-web/components/ui/scroll-area';
import {FileIdBadge} from './FileIdBadge';
import {toTitleCase} from '@common/lib/utils';
import {ExtractedFile} from '../types';

type AnchorTransformationModalProps = {
  open: boolean;
  onClose: () => void;
  fileId: string;
  columnName: string;
  transformations: PayrollFileValueTransformation[];
  onChange: (transformations: PayrollFileValueTransformation[]) => void;
  extractedFiles: ExtractedFile[];
};

export const AnchorTransformationModal = memo(
  ({
    open,
    onClose,
    fileId,
    columnName,
    transformations,
    onChange,
    extractedFiles,
  }: AnchorTransformationModalProps) => {
    const [localTransformations, setLocalTransformations] =
      useState<PayrollFileValueTransformation[]>(transformations);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isAddingTransformation, setIsAddingTransformation] = useState(false);
    const [isSelectOpen, setIsSelectOpen] = useState(false);

    const sampleValue = useMemo(() => {
      const file = extractedFiles.find((f) => f.file.fileId === fileId);
      if (!file) return '';
      const column = file.columns.find((c) => c.name === columnName);
      if (!column) return '';
      return column.samples?.[0] || '';
    }, [extractedFiles, fileId, columnName]);

    const preview = useMemo(() => {
      const preValue = sampleValue;
      const postValue = applyValueTransformations(sampleValue, localTransformations);
      return {pre: preValue, post: postValue};
    }, [sampleValue, localTransformations]);

    useEffect(() => {
      if (open) {
        setLocalTransformations(transformations);
        setHasUnsavedChanges(false);
      }
    }, [open, transformations]);

    const addValueTransform = useCallback((type: string) => {
      setLocalTransformations((prev) => {
        const next = [...prev];
        if (type === 'NONE') next.push({type});
        if (
          type === 'TRIM' ||
          type === 'TO_UPPERCASE' ||
          type === 'TO_LOWERCASE' ||
          type === 'REMOVE_NON_NUMERIC' ||
          type === 'REMOVE_NON_ALPHABETIC' ||
          type === 'PARSE_NUMBER' ||
          type === 'CONVERT_BLANK_TO_EMPTY'
        ) {
          next.push({type});
        }
        if (type === 'VALUE_MAPPING') {
          next.push({type, mappings: [{from: '', to: ''}]});
        }
        if (type === 'DEFAULT_IF_EMPTY') {
          next.push({type, defaultValue: ''});
        }
        if (type === 'DEFAULT_UNLESS_VALUE') {
          next.push({type, unlessValue: '', defaultValue: ''});
        }
        if (type === 'PAD_STRING') {
          next.push({type, padPosition: 'START', padCharacter: '0', targetLength: 9});
        }
        if (type === 'SUBSTRING') {
          next.push({type, startIndex: 1, length: undefined});
        }
        if (type === 'SPLIT_AND_EXTRACT') {
          next.push({type, delimiter: ', ', partIndex: 1});
        }
        return next;
      });
      setHasUnsavedChanges(true);
    }, []);

    const removeValueTransformAt = useCallback((idx: number) => {
      setLocalTransformations((prev) => {
        const next = [...prev];
        next.splice(idx, 1);
        return next;
      });
      setHasUnsavedChanges(true);
    }, []);

    const updateValueMap = useCallback(
      (idx: number, mapIdx: number, key: 'from' | 'to', value: string) => {
        setLocalTransformations((prev) => {
          const next = [...prev];
          const m = {...next[idx]} as {mappings?: {from: string; to: string}[]};
          const arr = [...(m.mappings || [])];
          arr[mapIdx] = {...arr[mapIdx], [key]: value};
          m.mappings = arr;
          next[idx] = m as PayrollFileValueTransformation;
          return next;
        });
        setHasUnsavedChanges(true);
      },
      [],
    );

    const addValueMapRow = useCallback((idx: number) => {
      setLocalTransformations((prev) => {
        const next = [...prev];
        const m = {...next[idx]} as {mappings?: {from: string; to: string}[]};
        m.mappings = [...(m.mappings || []), {from: '', to: ''}];
        next[idx] = m as PayrollFileValueTransformation;
        return next;
      });
      setHasUnsavedChanges(true);
    }, []);

    const removeValueMapRow = useCallback((idx: number, mapIdx: number) => {
      setLocalTransformations((prev) => {
        const next = [...prev];
        const m = {...next[idx]} as {mappings?: {from: string; to: string}[]};
        const arr = [...(m.mappings || [])];
        arr.splice(mapIdx, 1);
        m.mappings = arr;
        next[idx] = m as PayrollFileValueTransformation;
        return next;
      });
      setHasUnsavedChanges(true);
    }, []);

    const updateTransformationField = useCallback(
      (idx: number, field: string, value: string | number | undefined) => {
        setLocalTransformations((prev) => {
          const next = [...prev];
          next[idx] = {...next[idx], [field]: value} as PayrollFileValueTransformation;
          return next;
        });
        setHasUnsavedChanges(true);
      },
      [],
    );

    const handleSave = useCallback(() => {
      onChange(localTransformations);
      setHasUnsavedChanges(false);
      onClose();
    }, [localTransformations, onChange, onClose]);

    const handleClose = useCallback(() => {
      if (hasUnsavedChanges) {
        const confirmed = window.confirm(
          'You have unsaved changes. Are you sure you want to close?',
        );
        if (confirmed) {
          onClose();
        }
      } else {
        onClose();
      }
    }, [hasUnsavedChanges, onClose]);

    return (
      <Sheet
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleClose();
        }}
      >
        <SheetContent className='w-full sm:max-w-2xl lg:max-w-3xl overflow-hidden flex flex-col'>
          <SheetHeader className='shrink-0'>
            <SheetTitle className='flex items-center gap-2 text-internal-brand-navy'>
              <Zap className='h-5 w-5 text-amber' />
              File Link Transformations
            </SheetTitle>
            <SheetDescription>
              Configure how values from{' '}
              <span className='font-semibold text-internal-brand-navy'>{columnName}</span> should be
              transformed for file linking.
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className='flex-1'>
            <div className='py-4 pr-4 space-y-6'>
              <Card className='border-gray-200 shadow-sm'>
                <CardHeader className='py-3 bg-gradient-to-r from-internal-brand-blue/5 to-internal-brand-blue/10 border-b'>
                  <CardTitle className='text-sm font-semibold text-internal-brand-navy flex items-center gap-2'>
                    <Info className='h-4 w-4 text-internal-brand-blue' />
                    Source Column
                  </CardTitle>
                </CardHeader>
                <CardContent className='p-4'>
                  <div className='flex items-center gap-3'>
                    <FileIdBadge fileId={fileId} />
                    <div>
                      <div className='font-medium text-internal-brand-navy'>{columnName}</div>
                      {sampleValue && (
                        <div className='text-sm text-gray-500'>
                          Example value: <span className='font-mono'>{sampleValue}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className='border-gray-200 shadow-sm'>
                <CardHeader className='bg-gradient-to-r from-amber/5 to-amber/10 border-b'>
                  <CardTitle className='text-lg font-semibold text-internal-brand-navy flex items-center gap-2'>
                    <Zap className='h-5 w-5 text-amber' />
                    Value Transformations
                  </CardTitle>
                </CardHeader>
                <CardContent className='p-6'>
                  <div className='space-y-6'>
                    <div className='flex items-center gap-2'>
                      {!isAddingTransformation ? (
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => {
                            setIsAddingTransformation(true);
                            setIsSelectOpen(true);
                          }}
                          className='border-amber text-amber hover:bg-amber hover:text-white'
                        >
                          <Plus className='h-4 w-4 mr-1' /> Add transformation
                        </Button>
                      ) : (
                        <Select
                          open={isSelectOpen}
                          onOpenChange={setIsSelectOpen}
                          onValueChange={(value) => {
                            addValueTransform(value);
                            setIsAddingTransformation(false);
                            setIsSelectOpen(false);
                          }}
                        >
                          <SelectTrigger className='w-72'>
                            <SelectValue placeholder='Choose a transformation…' />
                          </SelectTrigger>
                          <SelectContent>
                            {VALUE_TRANSFORMATION_OPTIONS.map((option) => (
                              <SelectItem key={option.type} value={option.type}>
                                <div className='flex flex-col'>
                                  <span className='font-medium'>{option.label}</span>
                                  <span className='text-xs text-gray-600'>
                                    {option.description}
                                  </span>
                                  {option.example && (
                                    <span className='font-mono text-xs text-gray-600'>
                                      Example: {option.example}
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    {localTransformations.length === 0 ? (
                      <div className='text-center py-8 text-internal-brand-gray'>
                        <Zap className='h-12 w-12 mx-auto mb-3 text-internal-brand-gray-light' />
                        <div className='font-medium mb-1'>No transformations configured</div>
                        <div className='text-sm'>
                          Add transformations above to modify how values are matched between files
                        </div>
                      </div>
                    ) : (
                      <div className='space-y-4'>
                        <div className='flex items-center gap-2 mb-4'>
                          <div className='text-sm font-medium text-internal-brand-navy'>
                            Transformation Pipeline
                          </div>
                          <div className='flex-1 h-px bg-gray-200'></div>
                          <div className='text-xs text-internal-brand-gray'>
                            {localTransformations.length} step
                            {localTransformations.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div className='space-y-3'>
                          {localTransformations.map((vt, idx) => (
                            <div key={idx} className='flex items-start gap-3'>
                              <div className='flex flex-col items-center'>
                                <div className='w-8 h-8 bg-amber/10 rounded-full flex items-center justify-center text-amber font-semibold text-sm'>
                                  {idx + 1}
                                </div>
                                {idx < localTransformations.length - 1 && (
                                  <ArrowDown className='h-4 w-4 text-amber mt-2' />
                                )}
                              </div>
                              <Card className='flex-1 border-amber/20'>
                                <CardContent className='px-4 py-3'>
                                  <div className='flex items-center justify-between'>
                                    <div className='flex items-center gap-2'>
                                      <Zap className='h-4 w-4 text-amber' />
                                      <div className='text-sm text-amber font-medium'>
                                        {VALUE_TRANSFORMATION_OPTIONS.find(
                                          (o) => o.type === vt.type,
                                        )?.label || toTitleCase(vt.type.replace(/_/g, ' '))}
                                      </div>
                                    </div>
                                    <Button
                                      variant='ghost'
                                      size='sm'
                                      onClick={() => removeValueTransformAt(idx)}
                                      className='text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700'
                                    >
                                      <X className='h-4 w-4' />
                                    </Button>
                                  </div>
                                  {renderTransformationConfig(
                                    vt,
                                    idx,
                                    updateTransformationField,
                                    updateValueMap,
                                    addValueMapRow,
                                    removeValueMapRow,
                                  )}
                                </CardContent>
                              </Card>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              <Card className='border-emerald/20 bg-emerald/5'>
                <CardHeader className='py-4 bg-gradient-to-r from-emerald/10 to-emerald/20 border-b border-emerald/20'>
                  <CardTitle className='text-sm font-semibold text-internal-brand-navy flex items-center gap-2'>
                    <Eye className='h-4 w-4 text-emerald' />
                    Live Preview
                  </CardTitle>
                </CardHeader>
                <CardContent className='p-6'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                    <div className='space-y-2'>
                      <Label className='text-sm font-medium text-internal-brand-navy'>
                        Original Value
                      </Label>
                      <div className='p-3 bg-white border border-gray-200 rounded-lg font-mono text-sm'>
                        {preview.pre || (
                          <span className='text-gray-400'>No preview data available</span>
                        )}
                      </div>
                    </div>
                    <div className='space-y-2'>
                      <Label className='text-sm font-medium text-internal-brand-navy'>
                        After Transformation
                      </Label>
                      <div className='p-3 bg-white border border-gray-200 rounded-lg font-mono text-sm'>
                        {preview.post || (
                          <span className='text-gray-400'>No preview data available</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
          <SheetFooter className='shrink-0 pt-2 gap-2'>
            <Button variant='outline' onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className='bg-internal-brand-blue hover:bg-internal-brand-blue/90 text-white'
            >
              <Check className='h-4 w-4 mr-2' />
              {hasUnsavedChanges ? 'Save Changes' : 'Done'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  },
);

function renderTransformationConfig(
  vt: PayrollFileValueTransformation,
  idx: number,
  updateField: (idx: number, field: string, value: string | number | undefined) => void,
  updateValueMap: (idx: number, mapIdx: number, key: 'from' | 'to', value: string) => void,
  addValueMapRow: (idx: number) => void,
  removeValueMapRow: (idx: number, mapIdx: number) => void,
) {
  if (
    vt.type !== 'VALUE_MAPPING' &&
    vt.type !== 'DEFAULT_IF_EMPTY' &&
    vt.type !== 'DEFAULT_UNLESS_VALUE' &&
    vt.type !== 'PAD_STRING' &&
    vt.type !== 'SUBSTRING' &&
    vt.type !== 'SPLIT_AND_EXTRACT'
  ) {
    return null;
  }

  return (
    <div className='mt-2 space-y-2'>
      {vt.type === 'VALUE_MAPPING' ? (
        <>
          <Button variant='outline' size='sm' onClick={() => addValueMapRow(idx)}>
            <Plus className='h-4 w-4 mr-1' /> Add Mapping
          </Button>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(vt.mappings || []).map((m, mi) => (
                <TableRow key={mi}>
                  <TableCell>
                    <Input
                      value={m.from}
                      onChange={(e) => updateValueMap(idx, mi, 'from', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={m.to}
                      onChange={(e) => updateValueMap(idx, mi, 'to', e.target.value)}
                    />
                  </TableCell>
                  <TableCell className='text-right'>
                    <Button
                      variant='ghost'
                      size='sm'
                      className='text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700'
                      onClick={() => removeValueMapRow(idx, mi)}
                    >
                      <X className='h-4 w-4' />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      ) : vt.type === 'DEFAULT_IF_EMPTY' ? (
        <div className='flex items-center gap-2'>
          <Input
            value={vt.defaultValue || ''}
            placeholder='Enter default value'
            onChange={(e) => updateField(idx, 'defaultValue', e.target.value)}
          />
        </div>
      ) : vt.type === 'DEFAULT_UNLESS_VALUE' ? (
        <div className='flex flex-wrap items-end gap-3'>
          <div className='space-y-1'>
            <Label className='text-xs text-gray-600'>Unless value equals</Label>
            <Input
              value={vt.unlessValue || ''}
              placeholder='e.g., 0'
              className='w-32'
              onChange={(e) => updateField(idx, 'unlessValue', e.target.value)}
            />
          </div>
          <div className='space-y-1'>
            <Label className='text-xs text-gray-600'>Change to</Label>
            <Input
              value={vt.defaultValue || ''}
              placeholder='e.g., Y'
              className='w-32'
              onChange={(e) => updateField(idx, 'defaultValue', e.target.value)}
            />
          </div>
        </div>
      ) : vt.type === 'PAD_STRING' ? (
        <div className='flex flex-wrap items-end gap-3'>
          <div className='space-y-1'>
            <Label className='text-xs text-gray-600'>Position</Label>
            <Select
              value={vt.padPosition || 'START'}
              onValueChange={(value: 'START' | 'END') => updateField(idx, 'padPosition', value)}
            >
              <SelectTrigger className='w-28'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='START'>Start</SelectItem>
                <SelectItem value='END'>End</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-1'>
            <Label className='text-xs text-gray-600'>Pad Character</Label>
            <Input
              value={vt.padCharacter || ''}
              placeholder='0'
              maxLength={1}
              className='w-20 text-center'
              onChange={(e) => updateField(idx, 'padCharacter', e.target.value.slice(0, 1))}
            />
          </div>
          <div className='space-y-1'>
            <Label className='text-xs text-gray-600'>Target Length</Label>
            <Input
              type='number'
              value={vt.targetLength ?? ''}
              placeholder='9'
              min={1}
              max={100}
              className='w-24'
              onChange={(e) => {
                const parsed = parseInt(e.target.value, 10);
                updateField(
                  idx,
                  'targetLength',
                  isNaN(parsed) ? 1 : Math.min(100, Math.max(1, parsed)),
                );
              }}
            />
          </div>
        </div>
      ) : vt.type === 'SUBSTRING' ? (
        <div className='flex flex-wrap items-end gap-3'>
          <div className='space-y-1'>
            <Label className='text-xs text-gray-600'>Start Position</Label>
            <Input
              type='number'
              value={vt.startIndex ?? ''}
              placeholder='1'
              className='w-24'
              onChange={(e) => {
                const parsed = parseInt(e.target.value, 10);
                updateField(idx, 'startIndex', isNaN(parsed) ? 1 : parsed);
              }}
            />
            <div className='text-[10px] text-gray-500'>1 = first, -1 = last</div>
          </div>
          <div className='space-y-1'>
            <Label className='text-xs text-gray-600'>
              Length <span className='text-gray-400 font-normal'>(optional)</span>
            </Label>
            <Input
              type='number'
              value={vt.length ?? ''}
              placeholder='All'
              min={1}
              max={1000}
              className='w-24'
              onChange={(e) => {
                const parsed = parseInt(e.target.value, 10);
                updateField(
                  idx,
                  'length',
                  e.target.value === ''
                    ? undefined
                    : isNaN(parsed)
                      ? undefined
                      : Math.min(1000, Math.max(1, parsed)),
                );
              }}
            />
            <div className='text-[10px] text-gray-500'>Empty = to end</div>
          </div>
        </div>
      ) : vt.type === 'SPLIT_AND_EXTRACT' ? (
        <div className='flex flex-wrap items-end gap-3'>
          <div className='space-y-1'>
            <Label className='text-xs text-gray-600'>Delimiter</Label>
            <Input
              value={vt.delimiter ?? ''}
              placeholder=', '
              className='w-24'
              onChange={(e) => updateField(idx, 'delimiter', e.target.value)}
            />
            <div className='text-[10px] text-gray-500'>e.g., ", " or " "</div>
          </div>
          <div className='space-y-1'>
            <Label className='text-xs text-gray-600'>Part to Extract</Label>
            <Input
              type='number'
              value={vt.partIndex ?? ''}
              placeholder='1'
              className='w-24'
              onChange={(e) => {
                const parsed = parseInt(e.target.value, 10);
                updateField(idx, 'partIndex', isNaN(parsed) ? 1 : parsed);
              }}
            />
            <div className='text-[10px] text-gray-500'>1 = first, -1 = last</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
